import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ReceiveReleasableLineDto } from '@/entities/receive';
import {
  CONFIRM_ARRIVAL_UNIT_MODE,
  ConfirmArrivalHandlingSummaryBanner,
  ConfirmArrivalQuantityCell,
  ConfirmArrivalQuantityFigure,
  ConfirmArrivalUnitToolbar,
  summarizeHandlingUnits,
  supportsHandlingUnits,
  useSecondaryUnitCells,
  type ConfirmArrivalUnitMode
} from '@/features/receive-confirm-arrival';

import { releaseLineKey, toBundleConfig } from '../lib/build-payload';
import type { ReleaseGoodsFormValues } from '../model/types';

interface ReleaseGoodsLinesTableProps {
  control: Control<ReleaseGoodsFormValues>;
  setValue: UseFormSetValue<ReleaseGoodsFormValues>;
  lines: ReceiveReleasableLineDto[];
  onResetQuantities: () => void;
}

type ReleaseQuantityField = 'goodQty' | 'damagedQty';

interface ReleaseGoodsLineRowProps {
  control: Control<ReleaseGoodsFormValues>;
  setValue: UseFormSetValue<ReleaseGoodsFormValues>;
  line: ReceiveReleasableLineDto;
  index: number;
  unitMode: ConfirmArrivalUnitMode;
  showDamaged: boolean;
  labels: Record<ReleaseQuantityField, string>;
  isSecondaryOpen: (key: string, field: ReleaseQuantityField) => boolean;
  onToggleSecondary: (key: string, field: ReleaseQuantityField) => void;
}

/** One line; subscribes to its own slice so typing re-renders this row alone. */
function ReleaseGoodsLineRow({
  control,
  setValue,
  line,
  index,
  unitMode,
  showDamaged,
  labels,
  isSecondaryOpen,
  onToggleSecondary
}: ReleaseGoodsLineRowProps) {
  const values = useWatch({ control, name: `lines.${index}` });
  const { errors } = useFormState({ control, name: `lines.${index}` });
  const bundle = useMemo(() => toBundleConfig(line), [line]);

  if (!values) return null;

  const key = releaseLineKey(line);
  const lineErrors = errors.lines?.[index];

  // Good and damaged are released from separate ledgers, so each is capped by
  // its own receipt figure and neither borrows from the other — as in legacy.
  const renderCell = (field: ReleaseQuantityField) => (
    <ConfirmArrivalQuantityCell
      id={`${key}-release-${field}`}
      label={`${line.lineReference} — ${labels[field]}`}
      line={bundle}
      unitMode={unitMode}
      value={values[field]}
      ceiling={line[field]}
      onChange={(inventoryQty) =>
        setValue(`lines.${index}.${field}`, Math.min(inventoryQty, line[field]), {
          shouldDirty: true,
          shouldValidate: true
        })
      }
      secondaryOpen={isSecondaryOpen(key, field)}
      onToggleSecondary={() => onToggleSecondary(key, field)}
      error={lineErrors?.[field]?.message}
    />
  );

  return (
    <TableRow className="align-top">
      <TableCell>
        <Badge variant="outline">{line.lineReference}</Badge>
      </TableCell>
      <TableCell>
        <ConfirmArrivalQuantityFigure
          line={bundle}
          unitMode={unitMode}
          quantity={line.goodQty + line.damagedQty}
          kind="total"
        />
      </TableCell>
      <TableCell>{renderCell('goodQty')}</TableCell>
      {showDamaged && (
        // Legacy renders the damaged input only where something arrived damaged.
        <TableCell>{line.damagedQty > 0 ? renderCell('damagedQty') : '—'}</TableCell>
      )}
      <TableCell>
        <ConfirmArrivalQuantityFigure
          line={bundle}
          unitMode={unitMode}
          quantity={line.goodQty + line.damagedQty - values.goodQty - values.damagedQty}
          kind="remaining"
        />
      </TableCell>
    </TableRow>
  );
}

/** The release's handling-unit total; watches every line, so it lives apart from the rows. */
function ReleaseGoodsHandlingSummary({
  control,
  lines
}: {
  control: Control<ReleaseGoodsFormValues>;
  lines: ReceiveReleasableLineDto[];
}) {
  const values = useWatch({ control, name: 'lines' });
  const summary = useMemo(
    () =>
      summarizeHandlingUnits(
        lines.map((line, index) => ({
          line: toBundleConfig(line),
          inventoryQty: (values?.[index]?.goodQty ?? 0) + (values?.[index]?.damagedQty ?? 0)
        }))
      ),
    [lines, values]
  );

  return <ConfirmArrivalHandlingSummaryBanner summary={summary} />;
}

/**
 * Legacy `shipmentLines.xhtml`: what the receipt put on the ledgers, and how
 * much of it leaves — with the same unit calculator as the receipt table.
 */
export function ReleaseGoodsLinesTable({ control, setValue, lines, onResetQuantities }: ReleaseGoodsLinesTableProps) {
  const { t } = useTranslation();
  const { errors } = useFormState({ control, name: 'lines' });
  const [unitMode, setUnitMode] = useState<ConfirmArrivalUnitMode>(CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY);
  const { isSecondaryOpen, toggleSecondary, anySecondaryOpen } = useSecondaryUnitCells();

  const showDamaged = lines.some((line) => line.damagedQty > 0);
  const anyBundleLines = lines.some((line) => supportsHandlingUnits(toBundleConfig(line)));
  const labels: Record<ReleaseQuantityField, string> = {
    goodQty: t('dispatch.sane.cond.label', 'Good Condition'),
    damagedQty: t('dispatch.damaged.cond.label', 'Damaged Condition')
  };

  return (
    <div className="space-y-3">
      <ConfirmArrivalUnitToolbar
        showUnitSwitch={anyBundleLines}
        unitMode={unitMode}
        onUnitModeChange={setUnitMode}
        onResetQuantities={onResetQuantities}
        showsHandlingFigures={unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING || anySecondaryOpen}
      />

      <ReleaseGoodsHandlingSummary control={control} lines={lines} />

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-40">{t('receive.requestline.label', 'Consignment Line')}</TableHead>
              <TableHead className="min-w-32.5">{t('dispatch.instock.label', 'Quantity In Stock')}</TableHead>
              <TableHead className="min-w-42.5">{labels.goodQty}</TableHead>
              {showDamaged && <TableHead className="min-w-42.5">{labels.damagedQty}</TableHead>}
              <TableHead className="min-w-32.5">{t('receive.remainingquantity.label', 'Remaining Quantity')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line, index) => (
              <ReleaseGoodsLineRow
                key={releaseLineKey(line)}
                control={control}
                setValue={setValue}
                line={line}
                index={index}
                unitMode={unitMode}
                showDamaged={showDamaged}
                labels={labels}
                isSecondaryOpen={isSecondaryOpen}
                onToggleSecondary={toggleSecondary}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {errors.lines?.message && <p className="text-sm text-destructive">{errors.lines.message}</p>}
    </div>
  );
}
